/* eslint @atlaskit/ui-styling-standard/enforce-style-prop: 0 */
import { styled } from '@compiled/react';
import type { FC } from 'react';
import React, {
	Fragment,
	forwardRef,
	useEffect,
	useState,
	useMemo,
	useCallback,
	useRef,
} from 'react';
import ReactDOM from 'react-dom';
import uuid from 'uuid/v4';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { PopupComponentProps } from '@atlaskit/popup';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';

import { ConfluenceSiteAri } from '@atlassian/ari/confluence/site';
import type { SearchAIAnswerQueryType } from '@atlassian/search-ai';
import { AiBrand, SearchAIDialog, useDefinitionSupplier } from '@atlassian/search-ai';

import { getConfluenceContentAri } from '@confluence/content-types-utils/entry-points/getConfluenceContentAri';
import { useDefinitionRestrictionOptions } from '@confluence/contextual-reading-aids/entry-points/useDefinitionRestrictionOptions';
import { READING_AIDS_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component';
import { useInlineDialogCloseManager } from '@confluence/forge-ui/entry-points/useInlineDialogCloseManager';
import { useContentType } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys';
import { fg } from '@confluence/feature-gating';

import { useReadingAidsActions, useReadingAidsState } from './ReadingAidsStore';

const WORKSPACE_RESOURCE_TYPE = 'workspace';

const READING_AIDS_CONTEXT_MAX_CHARACTERS = 400;

export type SelectionRect = {
	top: number;
	left: number;
	width: number;
	height: number;
};

type ReadingAidsPopupProps = {
	selectedText: string;
	additionalContext?: string;
	selectionRange?: Range;
	selectionRect: SelectionRect | null;
	onResult: (result: SearchAIAnswerQueryType) => void;
	onLoad?: () => void;
	isReadingAidsOpen: boolean;
	onClose: () => void;
	contentId: string;
	popupPortalContainerId: string;
	isAutohighlighted?: boolean;
	/**
	 * if this popup is opened from the editor
	 */
	isEditor?: boolean;
	sessionId?: string;
};

const CustomPopupContainer = forwardRef<HTMLDivElement, PopupComponentProps>(
	({ children, ...props }, ref) => {
		return (
			<div {...props} ref={ref}>
				{children}
			</div>
		);
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-empty-styled-expression -- Ignored via go/DSP-18766
const PopupWrapper = styled.div({});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const CurrentSelectionSpan = styled.span<{
	top: number;
	left: number;
	width: number;
	height: number;
}>({
	opacity: 0,
	position: 'absolute',
	pointerEvents: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	top: `${(props: { top: number }) => props.top}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	left: `${(props: { left: number }) => props.left}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: `${(props: { width: number }) => props.width}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: `${(props: { height: number }) => props.height}px`,
});

export const extractReadingAidsContext = (range: Range | undefined): string => {
	const container = range?.startContainer.parentElement;
	if (!range || !container) {
		return '';
	}
	if (
		container.tagName === 'SPAN' ||
		container.tagName === 'A' ||
		container.tagName === 'STRONG' ||
		container.tagName === 'CODE'
	) {
		return container.parentElement
			? container.parentElement.textContent || container.innerText
			: '';
	}
	return container.textContent || container.innerText;
};

const LOADING_CONTAINER_ID = 'reading-aids-invisible-loading-portal';

export const InvisibleLoadingContainer = () => {
	return (
		<Fragment>
			<div
				style={{ position: 'absolute', top: 0, left: 0, width: 0, height: 0 }}
				id={LOADING_CONTAINER_ID}
			/>
		</Fragment>
	);
};

export const ReadingAidsPopup: FC<ReadingAidsPopupProps> = ({
	selectedText,
	additionalContext,
	selectionRange,
	selectionRect,
	isReadingAidsOpen,
	onClose,
	onLoad,
	contentId,
	popupPortalContainerId,
	isAutohighlighted,
	isEditor,
	sessionId,
}) => {
	const { cloudId, userId, edition, activationId, isRovoEnabled, isRovoLLMEnabled } =
		useSessionData();

	const [contentType] = useContentType();
	const spaceId = useSpaceId();
	const readingAidsSessionId = useRef(!!sessionId ? sessionId : uuid()).current;
	const { setAutohighlightPopupLoaded } = useReadingAidsActions();
	const { autohighlightHovered, showPopup, popupLoading } = useReadingAidsState();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const portalContainer = document.querySelector(`#${popupPortalContainerId}`);
	const [popupRef, setPopupRef] = useState<HTMLElement | null>(null);
	const [triggerRef, setTriggerRef] = useState<HTMLElement | null>(null);
	const documentRef = useRef<HTMLDivElement | null>(
		document.querySelector(`#${LOADING_CONTAINER_ID}`),
	);
	const answerLoadingRef = useRef(false);
	const [query, setQuery] = useState<string>('');
	const source = 'readingAids';

	const persistedAutoHighlightState = localStorage.getItemAsBoolean(
		PERSISTED_KEYS_ON_SERVER.PERSISTED_READING_AIDS_AUTO_HIGHLIGHT,
	);
	const [autoHighlightsDisabled, setAutoHighlightsDisabled] = useState<boolean>(
		persistedAutoHighlightState,
	);

	const cloudIdARI = ConfluenceSiteAri.create({
		siteId: cloudId,
	}).toString();
	const contentARI = contentType
		? getConfluenceContentAri({
				siteId: cloudId,
				resourceType: contentType,
				resourceId: contentId,
				activationId: '',
			})
		: '';
	const spaceARI = spaceId
		? getConfluenceContentAri({
				siteId: cloudId,
				resourceType: 'space',
				resourceId: spaceId,
				activationId: '',
			})
		: '';
	// We leave activationId as an empty string because reading aids will loaded in a content type with activationId
	const workspaceId = getConfluenceContentAri({
		siteId: cloudId,
		resourceType: WORKSPACE_RESOURCE_TYPE,
		resourceId: activationId,
		activationId: '',
	});

	const { fetchDefinition, loading: searchAILoading } = useDefinitionSupplier({
		workspaceId,
		cloudIdARI,
		fetchConfig: {
			entity_ari: contentARI,
		},
		productConfig: {
			contentId: contentARI,
			spaceId: spaceARI,
		},
		isDefinitionCurationEnabled: true,
		product: 'confluence',
	});

	useEffect(() => {
		if (!documentRef.current) {
			documentRef.current = document.querySelector(`#${LOADING_CONTAINER_ID}`);
		}
	}, [documentRef]);

	useEffect(() => {
		setQuery(selectedText);
	}, [selectedText]);

	// We want to parse only sentences containing the query after extracting the chunk
	// We will consider the sentence with the first occurrence of the query under 200 characters
	const additionalQueryContext = useMemo(() => {
		const contextChunk = additionalContext || extractReadingAidsContext(selectionRange);
		const sentenceRegex =
			/(?=[^])(?:\P{Sentence_Terminal}|\p{Sentence_Terminal}(?!['"`\p{Close_Punctuation}\p{Final_Punctuation}\s]))*(?:\p{Sentence_Terminal}+['"`\p{Close_Punctuation}\p{Final_Punctuation}]*|$)/guy;
		const contextSentences = contextChunk
			.match(sentenceRegex)
			?.filter((sentence) => !!sentence.match(query.replace(/[/\-\\^$*+?.()|[\]{}]/g, '\\$&')));
		if (contextSentences && contextSentences?.[0]?.length <= READING_AIDS_CONTEXT_MAX_CHARACTERS) {
			return contextSentences[0].trim();
		} else {
			return '';
		}
	}, [query, additionalContext, selectionRange]);

	const defineContentRef = (node: HTMLElement | null) => {
		setPopupRef(node);
	};

	const defineTriggerRef = (ref: any) => (node: HTMLElement | null) => {
		setTriggerRef(node);
		ref(node);
	};

	const onClosePopup = useCallback(() => {
		if (isAutohighlighted && autohighlightHovered && showPopup && popupLoading) {
			return;
		}
		onClose();
	}, [onClose, isAutohighlighted, autohighlightHovered, showPopup, popupLoading]);

	useInlineDialogCloseManager({ onClose: onClosePopup, popupRef, triggerRef });

	const toggleAutoHighlightsDisabled = useCallback(
		() => {
			// this boolean's flipped cause its looking for the old state
			if (autoHighlightsDisabled) {
				localStorage.setItem(PERSISTED_KEYS_ON_SERVER.PERSISTED_READING_AIDS_AUTO_HIGHLIGHT, false);
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'toggleDefinitionsOn',
						source,
						attributes: {
							readingAidsSessionId,
						},
					},
				}).fire();
			} else {
				localStorage.setItem(PERSISTED_KEYS_ON_SERVER.PERSISTED_READING_AIDS_AUTO_HIGHLIGHT, true);
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'toggleDefinitionsOff',
						source,
						attributes: {
							readingAidsSessionId,
						},
					},
				}).fire();
			}
			setAutoHighlightsDisabled(!autoHighlightsDisabled);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[autoHighlightsDisabled],
	);

	const readingAidsMenuOptions = [
		{
			type: 'toggle' as const,
			toggleState: autoHighlightsDisabled,
			label: {
				id: 'contextual-reading-aids.menu-option.disable-all.header',
				defaultMessage: 'Don’t recommend definitions at all',
				description:
					'Header text for the reading aids menu option to not disable auto highlighting for all pages',
			},
			onChange: toggleAutoHighlightsDisabled,
			description: {
				id: 'contextual-reading-aids.menu-option.disable-all.description',
				defaultMessage:
					'When this is on, we’ll no longer automatically highlight terms and acronyms on any page. Only you will see these changes.',
				description:
					'Description text for the reading aids menu option to not disable auto highlighting for all pages',
			},
		},
	];

	const definitionRestrictionOptions = useDefinitionRestrictionOptions(contentType);

	const curatedDefinitionScopeConfig = {
		workspaceId,
		definitionRestrictionOptions,
		productScopes: {
			contentARI,
			spaceARI,
		},
		cloudId,
		product: 'confluence',
	};

	const definitionCurationOptions = {
		curatedDefinitionScopeConfig,
	};

	const definitionLoadingConfig = {
		width: selectionRect?.width || 0,
	};

	if (!portalContainer || !selectionRect || !query) {
		return null;
	}

	// editor needs the top popup shift
	const triggerTop = isAutohighlighted && !isEditor ? selectionRect.top : selectionRect.top + 3; // offset to shift popup lower for define userflow

	// editor don't need the left popup shift
	const triggerLeft =
		isAutohighlighted || isEditor
			? selectionRect.left
			: selectionRect.left - selectionRect.width / 2; // offset to shift popup left for define userflow

	return ReactDOM.createPortal(
		<Popup
			autoFocus={false}
			testId="reading-aids-popup"
			content={({ update }) => (
				<PopupWrapper ref={defineContentRef}>
					<Fragment>
						<SearchAIDialog
							query={query}
							setQuery={setQuery}
							additionalQueryContext={additionalQueryContext}
							edition={edition?.toString()}
							getAIAnswer={fetchDefinition}
							onNavigate={() => {}}
							searchSessionId=""
							userDetails={{ id: userId || '' }}
							cloudId={cloudId}
							isReadingAids
							onClose={onClosePopup}
							answerLoadingRef={answerLoadingRef}
							readingAidsOptions={{
								contentARI,
								menuOptions: readingAidsMenuOptions,
								definitionCurationOptions,
								definitionLoadingConfig,
							}}
							source={source}
							sourceProduct="Confluence"
							extraAnalyticsAttributes={{
								contentId,
								readingAidsSessionId,
							}}
							baseQuery={query}
							onLoad={() => {
								if (isAutohighlighted) {
									void setAutohighlightPopupLoaded();
								}
								void update();
								onLoad?.();
							}}
							followUpsEnabled
							workspaceId={workspaceId}
							brand={isRovoEnabled ? AiBrand.ROVO : AiBrand.AI}
							definitionToggleEnabled={isAutohighlighted}
							showChatFollowUps={isRovoLLMEnabled}
							definitionExperienceName={
								isAutohighlighted ? 'contentAutohighlight' : 'contentDefineAction'
							}
						/>
					</Fragment>
					{!searchAILoading && (
						<ExperienceSuccess name={READING_AIDS_EXPERIENCE} attributes={{ contentId }} />
					)}
				</PopupWrapper>
			)}
			isOpen={isReadingAidsOpen}
			onClose={onClosePopup}
			placement="bottom-start"
			popupComponent={CustomPopupContainer}
			trigger={({ ref, ...triggerProps }) => (
				<CurrentSelectionSpan
					data-testid="reading-aids-selection-span"
					ref={defineTriggerRef(ref)}
					{...triggerProps}
					top={triggerTop}
					left={triggerLeft}
					width={selectionRect.width}
					height={selectionRect.height}
				/>
			)}
			zIndex={11}
			shouldRenderToParent={fg('should-render-to-parent-should-be-trueknowledge-di')}
		/>,
		portalContainer,
	);
};
